/* eslint-disable no-underscore-dangle */
import * as signalR from '@microsoft/signalr';
import useUserStore from 'stores/user';
import useSettingsStore from 'stores/settings';
import useChatTextStore from 'stores/chat-text';
import { useChatStore } from 'stores/chat';
import { text } from 'polyglot/polyglot';

export default class ChatManager {
  constructor() {
    if (this._instance) {
      // eslint-disable-next-line no-constructor-return
      return ChatManager.instance;
    }

    this.userState = useUserStore.getState();
    this.chatState = useChatStore.getState();
    this.chatTextStore = useChatTextStore.getState();

    this.chatconnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.userState.user.apiURL}/chathub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 5000, 20000, 50000])
      .build();

    this.setupEventHandlers();

    this.chatconnection
      .start()
      .then(() => {
        if (this.userState.user.nickname === '') {
          this.chatconnection.invoke(
            'JoinPublicChatAnonymous',
            this.userState.user.chatId
          );
        } else {
          this.chatconnection.invoke(
            'JoinChatHub',
            this.userState.user.nickname,
            this.userState.user.access_token
          );
        }
      })
      .catch((err) => console.error(err.toString()));
  }

  transformMessageInfo(message) {
    if (!message) return message;

    if (message.chatMessageId !== undefined) return message;

    const propertyMap = {
      CMID: 'chatMessageId',
      TP: 'type',
      ACT: 'action',
      MSG: 'message',
      CN: 'chatName',
      NCI: 'newChatIdentifier',
      NCDN: 'newChatDisplayName',
      UN: 'userName',
      ISP: 'isPrivate',
      ICH: 'isChatHost',
      CHI: 'chatHostImage',
      AID: 'avatarId',
      Sent: 'sent',
      UGUID: 'userGuid',
      RID: 'requestId',
      BW: 'blacklistedWord',
      CK: 'connectionKey',
      UID: 'userId',
      NOCU: 'numberOfChatUsers',
      APC: 'allowsPrivateChat',
      PND: 'pinned',
      NN: 'nickname',
    };

    const transformed = {};

    Object.keys(message).forEach((key) => {
      if (propertyMap[key]) {
        transformed[propertyMap[key]] = message[key];
      } else {
        transformed[key] = message[key];
      }
    });

    if (message.CP && Array.isArray(message.CP)) {
      transformed.chatParticipants = message.CP.map((participant) => {
        const transformedParticipant = {};
        Object.keys(participant).forEach((key) => {
          if (propertyMap[key]) {
            transformedParticipant[propertyMap[key]] = participant[key];
          } else {
            transformedParticipant[key] = participant[key];
          }
        });
        return transformedParticipant;
      });
    }

    if (message.CHM && Array.isArray(message.CHM)) {
      transformed.chatHistoricalMessages = message.CHM.map((msg) =>
        this.transformMessageInfo(msg)
      );
    }

    return transformed;
  }

  onEvent(eventName, callback) {
    this.chatconnection.on(eventName, (originalMessage) => {
      const transformedMessage = this.transformMessageInfo(originalMessage);
      callback(transformedMessage);
    });
  }

  setupEventHandlers() {
    // Start of reconnection if signal is lost
    this.chatconnection.onreconnecting((error) => {
      console.log('chat tries to reconnect with SignalR: ', error);
      this.chatState.setCurrentRoom(null);
      this.chatState.updateChatrooms([]);
    });

    this.chatconnection.onclose((err) => {
      console.log('chat disconnect error from SignalR: ', err);
    });

    this.chatconnection.onreconnected(() => {
      console.log('chat reconnection was made');
      if (this.userState.user.nickname === '') {
        this.chatconnection.invoke(
          'JoinPublicChatAnonymous',
          this.userState.user.chatId
        );
      } else {
        this.chatconnection.invoke(
          'JoinChatHub',
          this.userState.user.nickname,
          this.userState.user.access_token
        );
        const chatState = useChatStore.getState();
        if (chatState.connectionKey) {
          this.chatconnection.invoke(
            'JoinPublicChat',
            this.userState.user.chatId,
            this.userState.user.nickname,
            chatState.connectionKey
          );
        }
      }
    });
    // End of reconnection if signal is lost

    this.onEvent('HubWasJoined', (message) => {
      if (message.connectionKey) {
        this.chatState.setConnectionKey(message.connectionKey);
        this.chatconnection.invoke(
          'JoinPublicChat',
          this.userState.user.chatId,
          this.userState.user.nickname,
          message.connectionKey
        );
      }
    });

    this.onEvent('ChatCreatedByHost', (message) => {
      // console.log('ChatCreatedByHost: ', message);
      const chatStore = useChatStore.getState();
      const { chatrooms } = chatStore;
      const index = chatrooms.findIndex(
        (room) => room.roomId === message.newChatIdentifier
      );
      if (index === -1) {
        const newRoom = {
          roomId: message.newChatIdentifier,
          icon: !message.isPrivate ? -2 : message.avatarId,
          isPrivate: message.isPrivate,
          unreadCount: 0,
          messages: message.chatHistoricalMessages
            ? message.chatHistoricalMessages
            : [],
          chatName: message.newChatDisplayName,
          displayName: message.newChatDisplayName || '',
          chatParticipants: message.chatParticipants,
          isAdminChat: true,
        };
        this.chatState.addNewChatroom(newRoom);
        this.chatState.setCurrentRoom(newRoom);
      } else {
        // The room exists, can this happen, should be exchanged?
      }
    });

    this.onEvent('BlockedFromChat', (message) => {
      console.log('BlockedFromChat: ', message);
      this.chatTextStore.setUserIsBlocked(true);
    });

    this.onEvent('BlacklistedWord', () => {
      const userStore = useUserStore.getState();
      const settingsStore = useSettingsStore.getState();
      const chatStore = useChatStore.getState();
      const { chatrooms, currentRoom } = chatStore;
      const index = chatrooms.findIndex(
        (room) => room.roomId === currentRoom.roomId
      );
      if (index !== -1) {
        const newMessage = {
          avatarId: settingsStore.settings.userAvatar,
          isChatHost: false,
          message: text.t('blacklistedWordMessage'),
          isBlackListed: true,
          sent: Date.now(),
          userName: userStore.user.nickname,
        };
        chatStore.addNewMessage(index, newMessage);
        if (
          chatStore.currentRoom.roomId !== chatrooms[index].roomId ||
          chatStore.bingoRoomsIsVisible
        ) {
          chatStore.updateUnreadCount(index);
        }
        chatStore.setLastUpdated(Date.now());
      }
    });

    this.onEvent('ChatIsClosing', () => {
      // Message only for groupchat
      this.chatTextStore.setGroupChatIsClosed(true);
    });

    this.onEvent('ClosedChat', () => {
      this.chatTextStore.setGroupChatIsClosed(true);
    });

    this.onEvent('ChatClosed', () => {
      this.chatTextStore.setGroupChatIsClosed(true);
    });

    this.onEvent('ChatStarted', () => {
      // Message only for groupchat
      this.chatTextStore.setGroupChatIsClosed(false);
    });

    this.onEvent('ChatWasJoined', (message) => {
      const chatStore = useChatStore.getState();
      const { chatrooms } = chatStore;

      const index = chatrooms.findIndex(
        (room) => room.roomId === message.newChatIdentifier
      );
      if (index === -1) {
        // Should not display more than 8 chats
        if (chatrooms.length >= 8) return;

        const pinnedMessages = message.chatHistoricalMessages
          ? message.chatHistoricalMessages.filter((msg) => msg.pinned)
          : [];

        const newRoom = {
          roomId: message.newChatIdentifier || message.chatName,
          icon: !message.isPrivate ? -2 : message.avatarId,
          isPrivate: message.isPrivate,
          unreadCount: 0,
          messages: message.chatHistoricalMessages
            ? message.chatHistoricalMessages
            : [],
          pinnedMessages,
          chatName: message.newChatDisplayName,
          displayName: message.newChatDisplayName || '',
          chatParticipants: message.chatParticipants,
          isAdminChat: false,
          allowsPrivateChat:
            message.allowsPrivateChat === undefined
              ? true
              : message.allowsPrivateChat,
        };

        this.chatState.addNewChatroom(newRoom);
        this.chatState.setCurrentRoom(newRoom);
      } else {
        chatStore.updateParticipants(
          index,
          chatrooms[index].updateParticipantCount + 1
        );

        chatStore.addNewMessage(index, {
          type: 1,
          action: message.action,
          message: 'Joined_Chat',
          chatName: message.chatName,
          userName: message.userName,
          isPrivate: true,
          sent: Date.now(),
        });

        chatStore.setLastUpdated(Date.now());
      }
    });

    this.onEvent('ChatWasLeft', (message) => {
      const chatStore = useChatStore.getState();
      const { chatrooms } = chatStore;
      const index = chatrooms.findIndex(
        (room) => room.roomId === message.chatName
      );
      if (index !== -1) {
        chatStore.updateParticipants(
          index,
          chatrooms[index].updateParticipantCount - 1
        );

        chatStore.addNewMessage(index, {
          type: 1,
          action: message.action,
          message: 'Left_Chat',
          chatName: message.chatName,
          userName: message.userName,
          isPrivate: true,
          sent: Date.now(),
        });

        chatStore.setLastUpdated(Date.now());
      }
    });

    this.onEvent('ChatParticipants', (message) => {
      const chatStore = useChatStore.getState();
      const { chatrooms } = chatStore;
      const index = chatrooms.findIndex(
        (room) => room.roomId === message.chatName
      );
      if (index !== -1) {
        chatStore.updateParticipants(index, message.chatParticipants);
        chatStore.setLastUpdated(Date.now());
      }
    });

    this.onEvent('ChatRequest', (message) => {
      this.chatState.addActiveChatRequest(message);
    });

    this.onEvent('PrivateChatUserBlock', (message) => {
      const userName =
        message.userName ||
        Object.keys(message)
          .sort((a, b) => a - b)
          .map((key) => message[key])
          .join('');

      if (message.userName) {
        this.chatState.addPrivateChatUserBlockModalMsg({
          userName: message.userName,
          action: 'block',
        });
      } else if (userName && userName !== 'Already blocked') {
        this.chatState.addPrivateChatUserBlockModalMsg({
          userName,
          action: 'blockrequest',
        });
      }
    });

    this.onEvent('PrivateChatUserUnblock', (message) => {
      if (message.userName) {
        this.chatState.addPrivateChatUserBlockModalMsg({
          userName: message.userName,
          action: 'unblock',
        });
      }
    });

    this.onEvent('ChatUsersChanged', (message) => {
      const chatStore = useChatStore.getState();
      const { chatrooms } = chatStore;
      const index = chatrooms.findIndex(
        (room) => room.roomId === message.chatName
      );
      if (index !== -1) {
        chatStore.updateParticipantCount(index, message.numberOfChatUsers);
        chatStore.setLastUpdated(Date.now());
      }
    });

    this.onEvent('ReceiveMessage', (message) => {
      if (message.type === 3) {
        const chatStore = useChatStore.getState();
        const { chatrooms } = chatStore;
        const index = chatrooms.findIndex(
          (room) => room.roomId === message.chatName
        );
        if (index !== -1) {
          chatStore.addNewMessage(index, message);
          if (
            chatStore.currentRoom.roomId !== chatrooms[index].roomId ||
            chatStore.bingoRoomsIsVisible
          ) {
            chatStore.updateUnreadCount(index);
          }
          chatStore.setLastUpdated(Date.now());
        }
      }
    });

    this.onEvent('MessageUpdated', (message) => {
      if (message.type === 3) {
        const chatStore = useChatStore.getState();
        const { chatrooms } = chatStore;
        const index = chatrooms.findIndex(
          (room) => room.roomId === message.chatName
        );

        if (index !== -1) {
          chatStore.updateMessage(index, message);

          if (
            chatStore.currentRoom.roomId !== chatrooms[index].roomId ||
            chatStore.bingoRoomsIsVisible
          ) {
            chatStore.updateUnreadCount(index);
          }

          chatStore.setLastUpdated(Date.now());
        }
      }
    });
  }

  static get instance() {
    if (!this._instance) {
      this._instance = new ChatManager();
    }

    return this._instance;
  }

  //  Room should come from the user, isPrivate should depend on what room
  sendMessage(messageText, roomId, isPrivate) {
    const chatState = useChatStore.getState();
    try {
      this.chatconnection.invoke(
        'SendMessage',
        messageText,
        roomId,
        this.userState.user.nickname,
        chatState.connectionKey,
        isPrivate
      );
    } catch (err) {
      console.log('error on sending message to chat');
    }
  }

  requestChatParticipiantList(roomId) {
    try {
      this.chatconnection.invoke('GetChatUsers', roomId);
    } catch (err) {
      console.log('error on sending message to chat');
    }
  }

  requestPrivateChat(recipient) {
    const chatState = useChatStore.getState();
    try {
      this.chatconnection.invoke(
        'RequestPrivateChat',
        this.userState.user.nickname,
        recipient,
        chatState.connectionKey
      );
    } catch (err) {
      console.log('error on sending private chat request');
    }
  }

  answerRequest(reqId, answer) {
    const chatState = useChatStore.getState();
    try {
      this.chatconnection.invoke(
        'PrivateChatRequestResponse',
        reqId,
        answer,
        chatState.connectionKey
      );
    } catch (err) {
      console.log('error on sending private chat request');
    }
  }

  blockPrivateChatUser(recipient) {
    const chatState = useChatStore.getState();
    try {
      this.chatconnection.invoke(
        'BlockPrivateChatUser',
        this.userState.user.nickname,
        recipient,
        chatState.connectionKey
      );
    } catch (err) {
      console.log('error on sending private chat user block request');
    }
  }

  unblockPrivateChatUser(recipient) {
    const chatState = useChatStore.getState();
    try {
      this.chatconnection.invoke(
        'UnblockPrivateChatUser',
        this.userState.user.nickname,
        recipient,
        chatState.connectionKey
      );
    } catch (err) {
      console.log('error on sending private chat user unblock request');
    }
  }

  leaveChatRoom(chatId, nickname) {
    const chatState = useChatStore.getState();
    try {
      this.chatconnection.invoke(
        'LeaveChat',
        chatId,
        nickname,
        chatState.connectionKey
      );
    } catch (err) {
      console.log('error on sending private chat request');
    }
  }

  leaveAllChats(nickname) {
    const chatState = useChatStore.getState();
    this.chatconnection.invoke(
      'LeaveAllChats',
      nickname, //  nickname
      chatState.connectionKey // connection key
    );
  }
}
